'use client';

import { type ComponentProps, type ReactNode, useCallback, useState } from 'react';
import { Button } from '@bridebook/aura';
import { RoundedModalTitle } from '../rounded-modal/rounded-modal-header';
import { ConfirmationDrawerOrModalContent } from './confirmation-drawer-or-modal-content';
import { useImperativeDrawerOrModal } from './imperative-drawer-or-modal-context';

export type ConfirmationDrawerOrModalAutoCloseProps = {
  title: string;
  text?: string | ReactNode;
  primaryButtonText: string;
  /**
   * If the function returns a promise, the button will show a loading state until resolved.
   */
  onPrimaryButtonClick: () => void | Promise<void>;
  secondaryButtonText: string;
  /**
   * Optional. In most cases the secondary button only closes the modal.
   * If the function returns a promise, the button will show a loading state until resolved.
   */
  onSecondaryButtonClick?: () => void | Promise<void>;
  primaryButtonProps?: ComponentProps<typeof Button>;
  secondaryButtonProps?: ComponentProps<typeof Button>;
  showCloseButton?: boolean;
  closeModal: () => void | Promise<void>;
  variant?: ComponentProps<typeof ConfirmationDrawerOrModalContent>['variant'];
  headerTitleVariant?: ComponentProps<typeof RoundedModalTitle>['variant'];
  /**
   * Optional illustration above the title. Pass a React node to show custom art;
   * pass `null` to suppress the variant default. Omitted = use variant default.
   */
  headerIllustration?: ReactNode;
};

/**
 * Wraps ConfirmationDrawerOrModalContent with automatic modal closing and loading states.
 * Closes the modal after primary/secondary actions complete.
 */
export const ConfirmationDrawerOrModalAutoClose = ({
  title,
  text,
  primaryButtonText,
  onPrimaryButtonClick,
  secondaryButtonText,
  onSecondaryButtonClick,
  primaryButtonProps,
  secondaryButtonProps,
  variant = 'primary',
  headerTitleVariant = 'coupleside',
  headerIllustration,
  closeModal,
}: ConfirmationDrawerOrModalAutoCloseProps) => {
  const { dismissLockRef } = useImperativeDrawerOrModal();
  const [isLoadingPrimaryButton, setIsLoadingPrimaryButton] = useState(false);
  const [isLoadingSecondaryButton, setIsLoadingSecondaryButton] = useState(false);

  const handlePrimaryButtonClick = useCallback(async () => {
    dismissLockRef.current = true;
    setIsLoadingPrimaryButton(true);
    await onPrimaryButtonClick();
    setIsLoadingPrimaryButton(false);
    closeModal();
  }, [onPrimaryButtonClick, closeModal, dismissLockRef]);

  const handleSecondaryButtonClick = useCallback(async () => {
    dismissLockRef.current = true;
    setIsLoadingSecondaryButton(true);
    await onSecondaryButtonClick?.();
    setIsLoadingSecondaryButton(false);
    closeModal();
  }, [onSecondaryButtonClick, closeModal, dismissLockRef]);

  const disabled = isLoadingPrimaryButton || isLoadingSecondaryButton;

  return (
    <ConfirmationDrawerOrModalContent
      title={title}
      text={text}
      variant={variant}
      headerTitleVariant={headerTitleVariant}
      headerIllustration={headerIllustration}
      primaryButtonProps={{
        onClick: handlePrimaryButtonClick,
        loading: isLoadingPrimaryButton,
        disabled,
        children: primaryButtonText,
        ...primaryButtonProps,
      }}
      secondaryButtonProps={{
        onClick: handleSecondaryButtonClick,
        loading: isLoadingSecondaryButton,
        disabled,
        children: secondaryButtonText,
        ...secondaryButtonProps,
      }}
    />
  );
};
